import React, { useEffect, useState } from "react";
import { useConnection, useWallet } from "@solana/wallet-adapter-react";
import { PublicKey, Transaction } from "@solana/web3.js";
import { TOKEN_MINT, VESTING_PROGRAM_ID } from "utils/envs";
import { VestingAccount } from "types/account";
import { BorshCoder } from "@coral-xyz/anchor";
import vestingIdl from "../../idl/vesting.json";
import * as anchor from "@coral-xyz/anchor";
import {
  createAssociatedTokenAccountInstruction,
  getAssociatedTokenAddress,
  TOKEN_PROGRAM_ID,
} from "@solana/spl-token";
import { formatAmount } from "utils";
import { HIDDEN_VESTING_LIST } from "utils/constants";

const PROGRAM_ID = new PublicKey(VESTING_PROGRAM_ID);

interface VestingData {
  tgeDate: Date;
  startDate: Date;
  endDate: Date;
  nextRelease: Date;
  totalAmount: number;
  tgeAmount: number;
  amount: number;
  vestingId: number;
  vestingPda: PublicKey;
}

const parseVestingData = (
  vestingAccount: VestingAccount,
  vestingId: number,
  vestingPda: PublicKey
): VestingData => {
  const tgeDate = new Date(vestingAccount.tgeTs * 1000);
  const startDate = new Date(vestingAccount.startVestingTs * 1000);
  const endDate = new Date(vestingAccount.endVestingTs * 1000);
  const totalAmount = vestingAccount.totalAmount.toNumber() / 10 ** 9;
  const tgeAmount = vestingAccount.tgeAmount.toNumber() / 10 ** 9;
  const { amount, nextRelease } = calculateClaimableAmount(vestingAccount);

  return {
    startDate,
    endDate,
    amount: amount / 10 ** 9,
    nextRelease,
    totalAmount,
    tgeDate,
    tgeAmount,
    vestingId,
    vestingPda,
  };
};

const calculateClaimableAmount = (
  vestingAccount: VestingAccount
): { amount: number; nextRelease: Date } => {
  const startVestingTs = vestingAccount.startVestingTs.toNumber() * 1000;
  const endVestingTs = vestingAccount.endVestingTs.toNumber() * 1000;
  const tgeTimestamp = vestingAccount.tgeTs.toNumber() * 1000;
  const tgeAmount = vestingAccount.tgeAmount.toNumber();
  const totalAmount = vestingAccount.totalAmount.toNumber();
  const totalSteps = vestingAccount.totalSteps.toNumber();
  const vestedAmount = vestingAccount.vestedAmount.toNumber();

  let nextRelease = new Date(tgeTimestamp);
  const now = Date.now();
  if (now < tgeTimestamp) {
    return { amount: 0, nextRelease };
  }

  let vestableAmount = tgeAmount;

  if (now >= tgeTimestamp && totalSteps) {
    const elapsed = Math.min(now, endVestingTs) - tgeTimestamp;
    const totalDuration = endVestingTs - tgeTimestamp;

    const completedSteps = Math.round((elapsed * totalSteps) / totalDuration);
    let steps = Math.min(completedSteps, totalSteps);
    if (steps === 0) {
      steps += 1;
    }
    nextRelease =
      steps === totalSteps
        ? new Date(endVestingTs)
        : new Date(tgeTimestamp + (totalDuration / totalSteps) * steps);

    const linearVestingAmount = totalAmount - tgeAmount;
    const stepAmount = linearVestingAmount / totalSteps;
    const vestablePortion = stepAmount * steps;

    vestableAmount += vestablePortion;
  }

  vestableAmount = Math.min(vestableAmount, totalAmount);
  return { amount: vestableAmount - vestedAmount, nextRelease };
};

export function VestingList() {
  const {
    publicKey,
    signTransaction,
    signAllTransactions,
    sendTransaction,
    connected,
  } = useWallet();
  const { connection } = useConnection();

  const [isEligible, setIsEligible] = useState<boolean | undefined>(undefined);
  const [vestingData, setVestingData] = useState<VestingData[]>([]);
  const [loadingMap, setLoadingMap] = useState<{ [key: number]: boolean }>({});
  const [successMap, setSuccessMap] = useState<{ [key: number]: string }>({});

  const getProgram = (publicKey: PublicKey) => {
    const provider = new anchor.AnchorProvider(
      connection,
      {
        publicKey,
        signTransaction: signTransaction!,
        signAllTransactions: signAllTransactions!,
      },
      { commitment: "confirmed" }
    );

    return new anchor.Program(vestingIdl as any, PROGRAM_ID, provider);
  };

  useEffect(() => {
    const checkEligible = async (publicKey: PublicKey) => {
      const [userStatePda] = PublicKey.findProgramAddressSync(
        [Buffer.from("user_state"), publicKey.toBuffer()],
        new PublicKey(VESTING_PROGRAM_ID)
      );

      const program = getProgram(publicKey);

      try {
        const userState = (await program.account.userState.fetch(
          userStatePda
        )) as {
          userVestingSeq: anchor.BN;
        };

        const vestingSeq = userState.userVestingSeq;
        console.log(
          "✅ user_state already initialized, vestingSeq:",
          vestingSeq.toString()
        );

        const vestingPdas: PublicKey[] = [];
        for (let i = 0; i <= vestingSeq - 1; i++) {
          const [vestingPda] = PublicKey.findProgramAddressSync(
            [
              Buffer.from("vesting"),
              publicKey.toBuffer(),
              new PublicKey(TOKEN_MINT).toBuffer(),
              new anchor.BN(i).toArrayLike(Buffer, "le", 8),
            ],
            new PublicKey(VESTING_PROGRAM_ID)
          );

          if (!HIDDEN_VESTING_LIST.includes(vestingPda.toBase58())) {
            vestingPdas.push(vestingPda);
          }
        }

        const accountInfos =
          await connection.getMultipleAccountsInfo(vestingPdas);
        if (!accountInfos) {
          setIsEligible(false);
          return;
        }

        setIsEligible(true);
        const coder = new BorshCoder(vestingIdl as any);
        const vestingDataList: VestingData[] = [];

        for (let i = 0; i < accountInfos.length; i++) {
          const accountInfo = accountInfos[i];
          if (accountInfo) {
            const decodedAccount: VestingAccount = coder.accounts.decode(
              "VestingAccount",
              accountInfo.data
            );
            const data = parseVestingData(decodedAccount, i, vestingPdas[i]);
            vestingDataList.push(data);
          }
        }

        setVestingData(vestingDataList);
      } catch (e) {
        setIsEligible(false);
      }
    };

    if (publicKey) {
      checkEligible(publicKey);
    }
  }, [connection, publicKey]);

  if (!publicKey || isEligible === undefined) {
    return null;
  }

  const onClaim = async (vestingId: number, vestingPda: PublicKey) => {
    if (!publicKey || !connected) return;

    setLoadingMap((prev) => ({ ...prev, [vestingId]: true }));
    setSuccessMap((prev) => ({ ...prev, [vestingId]: "" }));

    const program = getProgram(publicKey);
    const tx = new anchor.web3.Transaction();

    const vestingTokenAccount = await getAssociatedTokenAddress(
      new PublicKey(TOKEN_MINT),
      vestingPda,
      true // allowOwnerOffCurve = true for PDA
    );

    console.log("vesting PDA", vestingPda.toBase58());

    const beneficiaryTokenAccount = await getAssociatedTokenAddress(
      new PublicKey(TOKEN_MINT),
      publicKey,
      true
    );

    console.log("beneficiaryTokenAccount", beneficiaryTokenAccount.toBase58());

    const accountInfo = await connection.getAccountInfo(
      beneficiaryTokenAccount
    );

    if (!accountInfo) {
      console.log("ATA not found => creating...");
      // If ATA does not exist, create instruction
      const createATAIx = createAssociatedTokenAccountInstruction(
        publicKey, // Payer for the rent
        beneficiaryTokenAccount, // ATA address
        publicKey, // Owner of ATA
        new PublicKey(TOKEN_MINT) // Mint address
      );

      tx.add(createATAIx);
    }

    console.log("Start claim...");
    const signature = await program.methods
      .claim(new anchor.BN(vestingId))
      .accounts({
        beneficiaryTokenAccount,
        beneficiary: publicKey,
        vestingTokenAccount,
        vesting: vestingPda,
        tokenProgram: TOKEN_PROGRAM_ID,
      })
      .instruction();

    tx.add(signature);
    const txSig = await sendTransaction(tx, connection);
    console.log("✅ Claim successfully:", txSig);

    setSuccessMap((prev) => ({
      ...prev,
      [vestingId]: "Claim successful!",
    }));

    setTimeout(() => {
      setSuccessMap((prev) => ({ ...prev, [vestingId]: "" }));
    }, 3000);
  };

  const isClaimable = (data: VestingData) => {
    return data.amount !== 0 && Date.now() >= data.nextRelease.getTime();
  };

  return (
    <div className="bit-card">
      {isEligible && vestingData.length > 0 ? (
        <>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "16px",
            }}
          >
            {vestingData.map((data, index) => (
              <div
                key={data.vestingId}
                style={{
                  border: "1px solid #ccc",
                  borderRadius: "8px",
                  padding: "16px",
                  minWidth: "400px",
                  maxWidth: "500px",
                  flex: "1 1 300px",
                  boxSizing: "border-box",
                }}
              >
                <div>
                  <div>
                    <h2 className="bit-title text-1xl mb-8">
                      Vesting Schedule #{index + 1}
                    </h2>
                    <p className="bit-text">
                      TGE: {data.tgeDate.toLocaleDateString()}
                    </p>
                    <p className="bit-text">
                      Total Amount: {formatAmount(data.totalAmount)} AVA
                    </p>
                    <p className="bit-text">
                      TGE Amount: {formatAmount(data.tgeAmount)} AVA
                    </p>
                    <p className="bit-text">
                      Vesting Amount: {formatAmount(data.amount)} AVA
                    </p>
                  </div>
                  <div style={{ textAlign: "left" }}>
                    <p className="bit-text">
                      Start: {data.startDate.toLocaleDateString()}
                    </p>
                    <p className="bit-text">
                      Next Release: {data.nextRelease.toLocaleDateString()}
                    </p>
                    <p className="bit-text">
                      End: {data.endDate.toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <button
                  disabled={loadingMap[data.vestingId] || !isClaimable(data)}
                  onClick={() => onClaim(data.vestingId, data.vestingPda)}
                  className="bit-menu-item"
                  style={{
                    marginTop: "12px",
                    pointerEvents:
                      loadingMap[data.vestingId] || !isClaimable(data)
                        ? "none"
                        : "auto",
                    opacity:
                      loadingMap[data.vestingId] || !isClaimable(data)
                        ? 0.5
                        : 1,
                    cursor:
                      loadingMap[data.vestingId] || !isClaimable(data)
                        ? "not-allowed"
                        : "pointer",
                  }}
                >
                  <span className="bit-menu-text">Claim</span>
                </button>

                {successMap[data.vestingId] && (
                  <div
                    style={{
                      backgroundColor: "#d4edda",
                      color: "#155724",
                      padding: "10px 16px",
                      borderRadius: "8px",
                      marginTop: "12px",
                      border: "1px solid #c3e6cb",
                      fontSize: "10px",
                    }}
                  >
                    {successMap[data.vestingId]}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      ) : (
        <h2 className="bit-title text-2xl mb-8">
          Sorry, you are not eligible!
        </h2>
      )}
    </div>
  );
}
